/**
 * Gallery Module — Horizontal Image Slideshow within a Story Panel
 *
 * Transforms <figure> elements inside a gallery panel into a
 * navigable slideshow with arrows, counter, swipe, and auto-srcset.
 *
 * Panel HTML (Ghost Editor):
 *   <section class="story-panel" data-story-type="gallery"
 *            data-audio="https://...mp3">   ← optional
 *     <h2>Optional Title</h2>
 *     <p>Optional text (stays fixed)</p>
 *     <figure>
 *       <img src="ghost-cdn-url" alt="...">
 *       <figcaption>Optional caption per image</figcaption>
 *     </figure>
 *     <figure>...</figure>
 *   </section>
 *
 * Features:
 *   - Text + title stay fixed, images swap with crossfade
 *   - Caption (figcaption) changes per slide
 *   - Auto srcset from Ghost CDN URLs (600/1000/1600w)
 *   - Arrow buttons, keyboard (←→), touch swipe
 *   - Counter "2 / 3"
 *   - Optional audio per panel (data-audio attribute)
 *     Play/Pause button, auto-pause on panel leave,
 *     auto-resume on return (-1s rewind for context)
 *
 * Events: Listens to story:panel:activated / deactivated
 * Registration: core.registerModule('gallery', this)
 *
 * File: assets/js/story/story-module-gallery.js
 */

(function(window) {
  'use strict';

  var DEBUG = false;
  if (window.location && window.location.search.indexOf('debug=story') !== -1) {
    DEBUG = true;
  }
  function log() {
    if (DEBUG && window.console) {
      var args = Array.prototype.slice.call(arguments);
      args.unshift('[Gallery]');
      console.log.apply(console, args);
    }
  }


  /* ========================================
     Constructor
     ======================================== */

  function StoryGallery(core) {
    this.core = core;
    this.galleries = [];
    this.activeGallery = null;
    this._hasAudio = false;
    this._boundKeyHandler = this._onKeyDown.bind(this);

    // MediaManager contract properties
    this.name = 'gallery';
    this.type = 'audio';
    this.isPlaying = false;
    this.panel = null;
    this.element = null;

    this._init();
    core.registerModule('gallery', this);
    log('Module registered');
  }


  /* ========================================
     Init — find and setup gallery panels
     ======================================== */

  StoryGallery.prototype._init = function() {
    var panels = document.querySelectorAll('.story-panel[data-story-type="gallery"]');

    for (var i = 0; i < panels.length; i++) {
      this._setupGallery(panels[i]);
    }

    var self = this;
    document.addEventListener('story:panel:activated', function(e) {
      self._onPanelActivated(e.detail);
    });
    document.addEventListener('story:panel:deactivated', function(e) {
      self._onPanelDeactivated(e.detail);
    });

    // Register with MediaManager if any gallery has audio
    if (this._hasAudio && window.StoryMediaManager) {
      window.StoryMediaManager.register(this);
      log('Registered with MediaManager');
    }

    log('Found', panels.length, 'gallery panels');
  };


  /* ========================================
     Setup — restructure DOM for one panel
     ======================================== */

  StoryGallery.prototype._setupGallery = function(panel) {
    var figures = panel.querySelectorAll('figure');
    if (figures.length === 0) return;

    // Separate text nodes from figure nodes. Hub buttons (data-goto-hub /
    // data-sg-goto-hub) must NOT land in the text box — they are re-appended
    // after the gallery below.
    var children = Array.prototype.slice.call(panel.children);
    var textNodes = [];
    var figureNodes = [];
    var hubNodes = [];

    children.forEach(function(child) {
      if (child.tagName === 'FIGURE') {
        figureNodes.push(child);
      } else if (child.hasAttribute('data-goto-hub') || child.hasAttribute('data-sg-goto-hub')) {
        hubNodes.push(child);
      } else {
        textNodes.push(child);
      }
    });

    if (figureNodes.length === 0) return;

    // -- Build gallery structure --
    var gallery = document.createElement('div');
    gallery.className = 'story-gallery';

    // Text area (fixed top)
    var textArea = null;
    if (textNodes.length > 0) {
      textArea = document.createElement('div');
      textArea.className = 'story-gallery__text';
      textNodes.forEach(function(node) { textArea.appendChild(node); });
    }

    // Audio setup (optional, triggered by data-audio on panel)
    var audioSrc = panel.getAttribute('data-audio');
    var audioState = null;
    if (audioSrc) {
      audioState = this._buildAudioUI();
      this._hasAudio = true;
    }

    // Top row: text (left) + audio (right) — or just text / just audio
    if (textArea || audioState) {
      var topRow = document.createElement('div');
      topRow.className = 'story-gallery__top';
      if (textArea) topRow.appendChild(textArea);
      if (audioState) topRow.appendChild(audioState.container);
      gallery.appendChild(topRow);
    }

    // Stage (arrows + slides)
    var stage = document.createElement('div');
    stage.className = 'story-gallery__stage';

    var prevBtn = document.createElement('button');
    prevBtn.className = 'story-gallery__arrow story-gallery__arrow--prev';
    prevBtn.setAttribute('aria-label', 'Vorheriges Bild');
    prevBtn.innerHTML = '&#8249;';
    stage.appendChild(prevBtn);

    var slidesWrap = document.createElement('div');
    slidesWrap.className = 'story-gallery__slides';

    figureNodes.forEach(function(fig, idx) {
      fig.classList.add('story-gallery__slide');
      if (idx === 0) fig.classList.add('story-gallery__slide--active');
      var img = fig.querySelector('img');
      if (img) {
        img.setAttribute('data-no-dynamic', '');  // C01 opt-out
        StoryGallery._addSrcset(img);
      }
      slidesWrap.appendChild(fig);
    });

    stage.appendChild(slidesWrap);

    var nextBtn = document.createElement('button');
    nextBtn.className = 'story-gallery__arrow story-gallery__arrow--next';
    nextBtn.setAttribute('aria-label', 'Nächstes Bild');
    nextBtn.innerHTML = '&#8250;';
    stage.appendChild(nextBtn);

    gallery.appendChild(stage);

    // Footer (caption + counter)
    var footer = document.createElement('div');
    footer.className = 'story-gallery__footer';

    var caption = document.createElement('div');
    caption.className = 'story-gallery__caption';
    footer.appendChild(caption);

    var counter = document.createElement('div');
    counter.className = 'story-gallery__counter';
    footer.appendChild(counter);

    gallery.appendChild(footer);

    // Replace panel content
    panel.innerHTML = '';
    panel.appendChild(gallery);

    // Re-insert hub buttons after gallery (inside panel, outside gallery).
    // Listeners survive: the nodes are moved, never cloned.
    hubNodes.forEach(function(btn) { panel.appendChild(btn); });

    // State object
    var state = {
      panel: panel,
      gallery: gallery,
      slides: figureNodes,
      slidesWrap: slidesWrap,
      captionEl: caption,
      counterEl: counter,
      prevBtn: prevBtn,
      nextBtn: nextBtn,
      current: 0,
      total: figureNodes.length,
      // Audio (null if no data-audio)
      audio: null,
      audioPlaying: false,
      audioManualPaused: false,
      audioUI: audioState,
      _fadeInterval: null,
      _textPagDone: false
    };

    // Wire up audio element if present
    if (audioSrc) {
      this._setupAudio(state, audioSrc);
    }

    this.galleries.push(state);

    // Click handlers
    var self = this;
    prevBtn.addEventListener('click', function(e) {
      e.stopPropagation();
      self._navigate(state, -1);
    });
    nextBtn.addEventListener('click', function(e) {
      e.stopPropagation();
      self._navigate(state, 1);
    });

    // Swipe
    this._setupSwipe(state, slidesWrap);

    // Tap-to-Zoom (mobile: tap image → fullscreen)
    this._setupZoom(state);

    // Initial UI
    this._updateUI(state);

    log('Gallery setup:', state.total, 'slides');
  };


  /* ========================================
     Mobile Text Pagination
     Replaces scrollbar with tap-to-page for
     the text area when content overflows.
     ======================================== */

  StoryGallery.prototype._setupTextPagination = function(state, gallery) {
    var topEl = gallery.querySelector('.story-gallery__top');
    if (!topEl) return;

    // Double-RAF: first waits for styles, second for layout to settle.
    // Single RAF was too early on iOS (panel visible but not yet laid out).
    requestAnimationFrame(function() {
      requestAnimationFrame(function() {
        var containerH = topEl.clientHeight;
        var scrollH = topEl.scrollHeight;

        // Panel not laid out yet — don't set _textPagDone so we retry
        if (containerH === 0) return;

        // Only paginate if text actually overflows
        if (scrollH <= containerH + 2) {
          state._textPagDone = true;
          return;
        }

        // Usable height: subtract padding-bottom (counted in clientHeight
        // but not available for visible text content)
        var cs = window.getComputedStyle(topEl);
        var padBottom = parseFloat(cs.paddingBottom) || 0;
        var threshold = containerH - padBottom;

        // Move audio player OUT of the paginated area so it's always
        // visible. Otherwise it scrolls away with the text content.
        var audioEl = topEl.querySelector('.story-gallery__audio');
        if (audioEl) {
          audioEl.remove();
          // Re-measure after removing audio (text-only now)
          containerH = topEl.clientHeight;
          scrollH = topEl.scrollHeight;
          threshold = containerH - (parseFloat(window.getComputedStyle(topEl).paddingBottom) || 0);
          if (scrollH <= containerH + 2) {
            // Text alone fits — no pagination needed, just keep audio outside
            topEl.parentNode.insertBefore(audioEl, topEl.nextSibling);
            state._textPagDone = true;
            return;
          }
        }

        var textEl = topEl.querySelector('.story-gallery__text');
        if (!textEl || textEl.children.length === 0) {
          if (audioEl) topEl.parentNode.insertBefore(audioEl, topEl.nextSibling);
          state._textPagDone = true;
          return;
        }

        // --- Pre-process: split oversized <p> elements ---
        // A <p> that alone exceeds the threshold must be split at sentence
        // boundaries into multiple <p> elements so each fits on a page.
        var rawKids = Array.prototype.slice.call(textEl.children);
        var kids = [];

        rawKids.forEach(function(el) {
          // Only split P elements; headings stay as-is
          if (el.tagName !== 'P') {
            kids.push(el);
            return;
          }
          // Measure single-P height
          rawKids.forEach(function(c) { c.style.display = 'none'; });
          el.style.display = '';
          var elH = topEl.scrollHeight;
          rawKids.forEach(function(c) { c.style.display = ''; });

          if (elH <= threshold + 2) {
            kids.push(el);
            return;
          }

          // Needs splitting at sentence boundaries
          var sentences = el.textContent.match(/[^.!?]+[.!?]+[\s]*/g) || [el.textContent];
          if (sentences.length <= 1) {
            // Single sentence too long — can't split further
            kids.push(el);
            return;
          }

          var groups = [];
          var currentGroup = [];
          sentences.forEach(function(sentence) {
            currentGroup.push(sentence);
            // Test fit with a temporary element
            var testP = document.createElement('p');
            testP.className = el.className;
            testP.textContent = currentGroup.join('').trim();
            rawKids.forEach(function(c) { c.style.display = 'none'; });
            textEl.appendChild(testP);
            var testH = topEl.scrollHeight;
            textEl.removeChild(testP);
            rawKids.forEach(function(c) { c.style.display = ''; });

            if (testH > threshold && currentGroup.length > 1) {
              groups.push(currentGroup.slice(0, -1).join('').trim());
              currentGroup = [sentence];
            }
          });
          if (currentGroup.length > 0) {
            groups.push(currentGroup.join('').trim());
          }

          if (groups.length <= 1) {
            kids.push(el);
            return;
          }

          // Replace original with split <p> elements
          el.style.display = 'none';
          groups.forEach(function(text) {
            var newP = document.createElement('p');
            newP.className = el.className;
            newP.textContent = text;
            textEl.insertBefore(newP, el);
            kids.push(newP);
          });
        });

        // --- Bin-packing: assign elements to pages ---
        var pages = [];
        var assigned = 0;

        while (assigned < kids.length) {
          // Hide all
          for (var h = 0; h < kids.length; h++) kids[h].style.display = 'none';

          // Show one-by-one starting from 'assigned'
          var pageStart = assigned;
          var j;
          for (j = assigned; j < kids.length; j++) {
            kids[j].style.display = '';
            if (topEl.scrollHeight > threshold) {
              kids[j].style.display = 'none';
              break;
            }
          }

          var pageEnd = j; // first that didn't fit, or kids.length

          // Single element taller than container — show it anyway
          if (pageEnd === pageStart) {
            kids[pageStart].style.display = '';
            pageEnd = pageStart + 1;
          }

          var pageIndices = [];
          for (var k = pageStart; k < pageEnd; k++) {
            pageIndices.push(k);
          }

          // Heading safety: don't leave headings alone on a page.
          // Try to include the next element — if it doesn't fit whole,
          // split it at sentence boundaries so heading + first sentences
          // share the page (no orphaned headings).
          var allHeadings = true;
          for (var c = 0; c < pageIndices.length; c++) {
            var tag = kids[pageIndices[c]].tagName;
            if (tag !== 'H1' && tag !== 'H2' && tag !== 'H3' && tag !== 'H4') {
              allHeadings = false;
              break;
            }
          }
          if (allHeadings && pageEnd < kids.length) {
            var nextEl = kids[pageEnd];
            nextEl.style.display = '';
            if (topEl.scrollHeight <= containerH) {
              // Fits whole — accept it
              pageIndices.push(pageEnd);
              pageEnd++;
            } else if (nextEl.tagName === 'P') {
              // Doesn't fit whole — split P at sentence boundaries
              nextEl.style.display = 'none';
              var sents = nextEl.textContent.match(/[^.!?]+[.!?]+[\s]*/g);
              if (sents && sents.length > 1) {
                // Find how many sentences fit WITH the heading
                var fitCount = 0;
                for (var s = 0; s < sents.length; s++) {
                  var tryP = document.createElement('p');
                  tryP.className = nextEl.className;
                  tryP.textContent = sents.slice(0, s + 1).join('').trim();
                  textEl.insertBefore(tryP, nextEl);
                  var tryH = topEl.scrollHeight;
                  textEl.removeChild(tryP);
                  if (tryH > containerH) break;
                  fitCount = s + 1;
                }
                if (fitCount > 0) {
                  // Create split: first part goes with heading
                  var partA = document.createElement('p');
                  partA.className = nextEl.className;
                  partA.textContent = sents.slice(0, fitCount).join('').trim();
                  textEl.insertBefore(partA, nextEl);

                  // Remainder becomes a new element for the next page
                  var partB = document.createElement('p');
                  partB.className = nextEl.className;
                  partB.textContent = sents.slice(fitCount).join('').trim();
                  textEl.insertBefore(partB, nextEl);

                  nextEl.style.display = 'none';

                  // Insert into kids array replacing the original
                  kids.splice(pageEnd, 1, partA, partB);

                  // Accept partA on this page
                  partA.style.display = '';
                  pageIndices.push(pageEnd);
                  pageEnd++;
                }
                // fitCount === 0: not even one sentence fits with heading → leave heading alone
              } else {
                // Single sentence or no sentences — can't split, heading stays alone
              }
            } else {
              // Not a P element — can't split, heading stays alone
              nextEl.style.display = 'none';
            }
          }

          pages.push(pageIndices);
          assigned = pageEnd;
        }

        var totalPages = pages.length;
        if (totalPages <= 1) {
          // Restore all elements — no pagination needed
          for (var r = 0; r < kids.length; r++) kids[r].style.display = '';
          if (audioEl) topEl.parentNode.insertBefore(audioEl, topEl.nextSibling);
          state._textPagDone = true;
          return;
        }

        // Success — pagination created
        state._textPagDone = true;
        topEl.style.overflowY = 'hidden';
        topEl.classList.add('is-paginated');

        var currentPage = 0;

        function showPage(page) {
          currentPage = page;
          var indices = pages[page];
          for (var i = 0; i < kids.length; i++) {
            var visible = indices.indexOf(i) !== -1;
            kids[i].style.display = visible ? '' : 'none';
            kids[i].style.marginBottom = ''; // reset
          }
          // Remove margin-bottom from last visible element — it's NOT
          // :last-child (hidden siblings follow), so CSS margin-bottom:0
          // doesn't apply. The extra margin wastes space at the bottom.
          var lastIdx = indices[indices.length - 1];
          kids[lastIdx].style.marginBottom = '0';
          topEl.scrollTop = 0;
          pagLabel.textContent = (page + 1) + ' / ' + totalPages;
          pagPrev.style.visibility = page === 0 ? 'hidden' : 'visible';
          pagNext.style.visibility = page >= totalPages - 1 ? 'hidden' : 'visible';
        }

        // Build pagination indicator — placed AFTER topEl (not inside)
        var pagEl = document.createElement('div');
        pagEl.className = 'story-gallery__text-pag';

        var pagPrev = document.createElement('button');
        pagPrev.className = 'text-pag__prev';
        pagPrev.textContent = '\u2039'; // ‹
        pagPrev.setAttribute('aria-label', 'Vorherige Seite');

        var pagLabel = document.createElement('span');
        pagLabel.className = 'text-pag__label';

        var pagNext = document.createElement('button');
        pagNext.className = 'text-pag__next';
        pagNext.textContent = '\u203A'; // ›
        pagNext.setAttribute('aria-label', 'Nächste Seite');

        pagEl.appendChild(pagPrev);
        pagEl.appendChild(pagLabel);
        pagEl.appendChild(pagNext);
        topEl.parentNode.insertBefore(pagEl, topEl.nextSibling);

        // Place audio after pagEl (always visible, on all pages)
        if (audioEl) {
          pagEl.parentNode.insertBefore(audioEl, pagEl.nextSibling);
        }

        // Navigation handlers
        pagPrev.addEventListener('click', function(e) {
          e.stopPropagation();
          if (currentPage > 0) showPage(currentPage - 1);
        });
        pagNext.addEventListener('click', function(e) {
          e.stopPropagation();
          if (currentPage < totalPages - 1) showPage(currentPage + 1);
        });
        pagLabel.addEventListener('click', function(e) {
          e.stopPropagation();
          if (currentPage < totalPages - 1) showPage(currentPage + 1);
        });

        showPage(0);

        // Store reference for cleanup
        state._textPag = { el: pagEl, topEl: topEl };

        log('Text pagination:', totalPages, 'pages (show/hide)', pages);
      });
    });
  };


  /* ========================================
     Navigation
     ======================================== */

  StoryGallery.prototype._navigate = function(state, direction) {
    var next = state.current + direction;
    if (next < 0 || next >= state.total) return;

    state.slides[state.current].classList.remove('story-gallery__slide--active');
    state.current = next;
    state.slides[state.current].classList.add('story-gallery__slide--active');

    this._updateUI(state);
  };

  StoryGallery.prototype._updateUI = function(state) {
    // Counter (hidden for single image)
    state.counterEl.textContent = state.total > 1
      ? (state.current + 1) + ' / ' + state.total
      : '';

    // Caption from active figcaption
    var fig = state.slides[state.current];
    var figcaption = fig.querySelector('figcaption');
    state.captionEl.innerHTML = figcaption ? figcaption.innerHTML : '';

    // Arrow visibility
    if (state.total <= 1) {
      state.prevBtn.style.display = 'none';
      state.nextBtn.style.display = 'none';
    } else {
      state.prevBtn.style.display = '';
      state.nextBtn.style.display = '';
      state.prevBtn.style.visibility = state.current === 0 ? 'hidden' : 'visible';
      state.nextBtn.style.visibility = state.current === state.total - 1 ? 'hidden' : 'visible';
    }

    log('Slide', state.current + 1, '/', state.total);
  };


  /* ========================================
     Swipe Support (Mobile)
     ======================================== */

  StoryGallery.prototype._setupSwipe = function(state, el) {
    var startX = 0;
    var startY = 0;
    var self = this;

    el.addEventListener('touchstart', function(e) {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });

    el.addEventListener('touchend', function(e) {
      var dx = e.changedTouches[0].clientX - startX;
      var dy = e.changedTouches[0].clientY - startY;

      // Horizontal swipe: min 50px, more horizontal than vertical
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
        if (dx < 0) {
          self._navigate(state, 1);  // swipe left → next
        } else {
          self._navigate(state, -1); // swipe right → prev
        }
      }
    }, { passive: true });
  };


  /* ========================================
     Keyboard (← →) when panel is active
     ======================================== */

  StoryGallery.prototype._onKeyDown = function(e) {
    if (!this.activeGallery) return;

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this._navigate(this.activeGallery, -1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this._navigate(this.activeGallery, 1);
    }
  };


  /* ========================================
     Panel Activation / Deactivation
     ======================================== */

  StoryGallery.prototype._onPanelActivated = function(detail) {
    var panel = detail.panel || detail;
    var state = this._findGallery(panel);
    if (!state) return;

    // Cancel any pending deferred autoplay from a previous activation.
    // Safari IO fires all panels at once — without this, multiple
    // setTimeout(0) callbacks would each start their gallery's audio.
    if (this._deferredAutoplay) {
      clearTimeout(this._deferredAutoplay);
      this._deferredAutoplay = null;
    }

    // Stop audio on previously active gallery before switching.
    // Also cancel any pending delayed autoplay timer (data-audio-delay).
    // Without this, Safari IO burst creates per-gallery timers that all
    // fire after the delay — the activeGallery check catches most, but
    // if the user scrolls to a different gallery within the delay window,
    // the wrong gallery's audio could start.
    var prev = this.activeGallery;
    if (prev && prev !== state) {
      if (prev._autoplayTimer) {
        clearTimeout(prev._autoplayTimer);
        prev._autoplayTimer = null;
      }
      if (prev.audio && prev.audioPlaying) {
        this._audioPause(prev);
        prev.audioPlaying = false;
      }
    }

    this.activeGallery = state;
    document.addEventListener('keydown', this._boundKeyHandler);

    // Mobile text pagination: defer to activation so panel has layout.
    // Note: _textPagDone is set INSIDE the async RAF callback, not here.
    // This allows retry if the panel wasn't laid out yet (containerH === 0).
    if (window.innerWidth <= 768 && !state._textPagDone) {
      this._setupTextPagination(state, state.gallery);
    }

    // Auto-resume audio if it was playing when panel was left
    if (state.audioPlaying && state.audio) {
      state.audio.currentTime = Math.max(0, state.audio.currentTime - 1.0);
      this._audioPlay(state);
    }
    // First-entry autoplay (unless data-audio-manual or manually paused).
    // Deferred via setTimeout(0) so that when Safari's IntersectionObserver
    // fires multiple panel:activated events synchronously, only the LAST
    // gallery's audio starts (earlier ones get clearTimeout'd above).
    else if (state.audio && !state.audioPlaying && !state.audioManualPaused
             && !panel.hasAttribute('data-audio-manual')
             && window.StoryMediaManager && window.StoryMediaManager.userGestureReceived) {
      var delaySec = parseFloat(panel.getAttribute('data-audio-delay'));
      var self = this;
      if (delaySec > 0) {
        state._autoplayTimer = setTimeout(function() {
          state._autoplayTimer = null;
          if (self.activeGallery === state && !state.audioManualPaused) {
            self._audioPlay(state);
          }
        }, delaySec * 1000);
      } else {
        this._deferredAutoplay = setTimeout(function() {
          self._deferredAutoplay = null;
          if (self.activeGallery === state && !state.audioManualPaused) {
            self._audioPlay(state);
          }
        }, 0);
      }
    }

    log('Gallery activated');
  };

  StoryGallery.prototype._onPanelDeactivated = function(detail) {
    var panel = detail.panel || detail;
    var state = this._findGallery(panel);
    if (!state) return;

    // Cancel pending autoplay delay
    if (state._autoplayTimer) {
      clearTimeout(state._autoplayTimer);
      state._autoplayTimer = null;
    }

    // Hard-stop audio immediately (don't rely on fade callback)
    if (state.audio && !state.audio.paused) {
      var wasPlaying = state.audioPlaying;
      state.audio.pause();
      state.audio.volume = 1;       // Reset volume for next play
      clearInterval(state._fadeInterval);
      // Update UI without triggering MediaManager notify
      if (state.audioUI) {
        state.audioUI.playIcon.style.display = '';
        state.audioUI.pauseIcon.style.display = 'none';
        state.audioUI.playBtn.setAttribute('aria-label', 'Audio abspielen');
      }
      state.audioPlaying = false;  // Reset: no auto-resume after deactivation
      this.isPlaying = false;
    }

    // Keep audioManualPaused state — if user manually paused,
    // respect that when they return to this panel.
    // It resets naturally when user clicks play again (line 693).

    if (this.activeGallery === state) {
      this.activeGallery = null;
      document.removeEventListener('keydown', this._boundKeyHandler);
    }
    log('Gallery deactivated');
  };

  StoryGallery.prototype._findGallery = function(panel) {
    for (var i = 0; i < this.galleries.length; i++) {
      if (this.galleries[i].panel === panel) return this.galleries[i];
    }
    return null;
  };


  /* ========================================
     Tap-to-Zoom (Mobile only)
     ======================================== */

  StoryGallery.prototype._setupZoom = function(state) {
    var self = this;
    var isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (!isMobile) return;

    state.slidesWrap.addEventListener('click', function(e) {
      // Only zoom if tap was on the image (not on arrows)
      if (e.target.tagName === 'IMG' || e.target.closest('.story-gallery__slide')) {
        if (window.innerWidth <= 768) {
          self._openZoom(state);
        }
      }
    });
  };

  StoryGallery.prototype._openZoom = function(state) {
    if (this._zoomOverlay) return;  // Already open

    var self = this;
    var overlay = document.createElement('div');
    overlay.className = 'story-gallery__zoom';

    // Prev arrow (gallery navigation inside zoom)
    var prevBtn = document.createElement('button');
    prevBtn.className = 'story-gallery__zoom-arrow story-gallery__zoom-arrow--prev';
    prevBtn.innerHTML = '&#8249;';
    prevBtn.setAttribute('aria-label', 'Vorheriges Bild');
    overlay.appendChild(prevBtn);

    // Content area (image + caption + counter)
    var content = document.createElement('div');
    content.className = 'story-gallery__zoom-content';

    var zoomImg = document.createElement('img');
    content.appendChild(zoomImg);

    var caption = document.createElement('div');
    caption.className = 'story-gallery__zoom-caption';
    content.appendChild(caption);

    var counter = document.createElement('div');
    counter.className = 'story-gallery__zoom-counter';
    content.appendChild(counter);

    overlay.appendChild(content);

    // Next arrow
    var nextBtn = document.createElement('button');
    nextBtn.className = 'story-gallery__zoom-arrow story-gallery__zoom-arrow--next';
    nextBtn.innerHTML = '&#8250;';
    nextBtn.setAttribute('aria-label', 'Nächstes Bild');
    overlay.appendChild(nextBtn);

    // Store zoom state
    this._zoomState = {
      img: zoomImg,
      caption: caption,
      counter: counter,
      prevBtn: prevBtn,
      nextBtn: nextBtn,
      state: state
    };

    // Fill content for current slide
    this._updateZoom();

    // Arrow handlers (stopPropagation prevents close)
    prevBtn.addEventListener('click', function(e) {
      e.stopPropagation();
      self._navigateZoom(-1);
    });
    nextBtn.addEventListener('click', function(e) {
      e.stopPropagation();
      self._navigateZoom(1);
    });

    // Swipe + tap handling
    var startX = 0, moved = false;

    overlay.addEventListener('touchstart', function(e) {
      startX = e.touches[0].clientX;
      moved = false;
    }, { passive: true });

    overlay.addEventListener('touchmove', function(e) {
      if (Math.abs(e.touches[0].clientX - startX) > 10) moved = true;
    }, { passive: true });

    overlay.addEventListener('touchend', function(e) {
      var dx = e.changedTouches[0].clientX - startX;
      var dy = e.changedTouches[0].clientY - startX;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
        if (dx < 0) self._navigateZoom(1);   // swipe left → next
        else self._navigateZoom(-1);          // swipe right → prev
      }
    }, { passive: true });

    // Tap to close (only if no swipe and not on arrows)
    overlay.addEventListener('click', function(e) {
      if (e.target.closest('.story-gallery__zoom-arrow')) return;
      if (moved) { moved = false; return; }
      self._closeZoom();
    });

    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';
    this._zoomOverlay = overlay;

    log('Zoom opened');
  };

  StoryGallery.prototype._navigateZoom = function(direction) {
    var zs = this._zoomState;
    if (!zs) return;

    // Navigate underlying gallery (updates state.current + UI)
    this._navigate(zs.state, direction);
    // Update zoom display
    this._updateZoom();
  };

  StoryGallery.prototype._updateZoom = function() {
    var zs = this._zoomState;
    if (!zs) return;

    var state = zs.state;
    var slide = state.slides[state.current];
    var img = slide.querySelector('img');

    if (img) {
      zs.img.src = img.src;
      zs.img.srcset = img.srcset || '';
      zs.img.sizes = '100vw';
      zs.img.alt = img.alt || '';
    }

    // Caption
    var figcaption = slide.querySelector('figcaption');
    zs.caption.innerHTML = figcaption ? figcaption.innerHTML : '';
    zs.caption.style.display = (figcaption && figcaption.textContent.trim()) ? '' : 'none';

    // Counter
    zs.counter.textContent = state.total > 1
      ? (state.current + 1) + ' / ' + state.total
      : '';

    // Arrow visibility
    if (state.total <= 1) {
      zs.prevBtn.style.display = 'none';
      zs.nextBtn.style.display = 'none';
    } else {
      zs.prevBtn.style.visibility = state.current === 0 ? 'hidden' : 'visible';
      zs.nextBtn.style.visibility = state.current === state.total - 1 ? 'hidden' : 'visible';
    }
  };

  StoryGallery.prototype._closeZoom = function() {
    if (!this._zoomOverlay) return;

    this._zoomOverlay.remove();
    this._zoomOverlay = null;
    this._zoomState = null;
    document.body.style.overflow = '';

    log('Zoom closed');
  };


  /* ========================================
     Auto srcset from Ghost CDN URLs
     ======================================== */

  StoryGallery._addSrcset = function(img) {
    var src = img.getAttribute('src') || '';
    if (src.indexOf('/content/images/') === -1) return;
    if (img.getAttribute('srcset')) return;
    if (src.indexOf('/images/size/') !== -1) return;

    var widths = [600, 1000, 1600];
    var srcset = widths.map(function(w) {
      return src.replace('/content/images/', '/content/images/size/w' + w + '/') + ' ' + w + 'w';
    }).join(', ');

    img.setAttribute('srcset', srcset);
    img.setAttribute('sizes', '(max-width: 768px) 100vw, 80vw');
  };


  /* ========================================
     Audio — UI Build
     ======================================== */

  StoryGallery.prototype._buildAudioUI = function() {
    var audioEl = document.createElement('div');
    audioEl.className = 'story-gallery__audio';

    var playBtn = document.createElement('button');
    playBtn.className = 'story-gallery__audio-btn';
    playBtn.setAttribute('aria-label', 'Audio abspielen');
    playBtn.innerHTML =
      '<svg class="story-gallery__audio-icon story-gallery__audio-icon--play" viewBox="0 0 24 24" aria-hidden="true">' +
        '<path d="M9 7l10 5-10 5V7z" fill="currentColor"/>' +
      '</svg>' +
      '<svg class="story-gallery__audio-icon story-gallery__audio-icon--pause" viewBox="0 0 24 24" aria-hidden="true" style="display:none">' +
        '<rect x="7" y="6" width="3.5" height="12" rx="1" fill="currentColor"/>' +
        '<rect x="13.5" y="6" width="3.5" height="12" rx="1" fill="currentColor"/>' +
      '</svg>';
    audioEl.appendChild(playBtn);

    var timeEl = document.createElement('span');
    timeEl.className = 'story-gallery__audio-time';
    timeEl.textContent = '0:00 / 0:00';
    audioEl.appendChild(timeEl);

    var progressWrap = document.createElement('div');
    progressWrap.className = 'story-gallery__audio-progress';
    var progressFill = document.createElement('div');
    progressFill.className = 'story-gallery__audio-fill';
    progressWrap.appendChild(progressFill);
    audioEl.appendChild(progressWrap);

    return {
      container: audioEl,
      playBtn: playBtn,
      timeEl: timeEl,
      progressFill: progressFill,
      playIcon: playBtn.querySelector('.story-gallery__audio-icon--play'),
      pauseIcon: playBtn.querySelector('.story-gallery__audio-icon--pause')
    };
  };


  /* ========================================
     Audio — Setup (wire Audio element + events)
     ======================================== */

  StoryGallery.prototype._setupAudio = function(state, audioSrc) {
    var audio = new Audio();
    audio.preload = 'metadata';
    audio.src = audioSrc;
    state.audio = audio;

    var self = this;
    var ui = state.audioUI;

    // Click handler: toggle play/pause
    ui.playBtn.addEventListener('click', function(e) {
      e.stopPropagation();
      if (audio.paused) {
        // Request MediaManager permission
        if (window.StoryMediaManager) {
          var allowed = window.StoryMediaManager.requestPlay(self);
          if (!allowed) return;
        }
        state.audioManualPaused = false;
        self._audioPlay(state);
      } else {
        state.audioManualPaused = true;
        self._audioPause(state);
      }
    });

    // Time update → display + progress bar
    audio.addEventListener('timeupdate', function() {
      ui.timeEl.textContent = StoryGallery._formatTime(audio.currentTime) +
        ' / ' + StoryGallery._formatTime(audio.duration || 0);
      var pct = audio.duration ? (audio.currentTime / audio.duration * 100) : 0;
      ui.progressFill.style.width = pct + '%';
    });

    // Duration loaded → update display
    audio.addEventListener('loadedmetadata', function() {
      ui.timeEl.textContent = '0:00 / ' + StoryGallery._formatTime(audio.duration);
    });

    // Audio ended → reset button
    audio.addEventListener('ended', function() {
      state.audioPlaying = false;
      self.isPlaying = false;
      ui.playIcon.style.display = '';
      ui.pauseIcon.style.display = 'none';
      ui.playBtn.setAttribute('aria-label', 'Audio abspielen');
      ui.progressFill.style.width = '0%';
      if (window.StoryMediaManager) {
        window.StoryMediaManager.notifyStop(self);
      }
      log('Audio ended');
    });

    log('Audio setup:', audioSrc);
  };


  /* ========================================
     Audio — Play / Pause
     ======================================== */

  StoryGallery.prototype._audioPlay = function(state) {
    var audio = state.audio;
    var ui = state.audioUI;
    if (!audio || !ui) return;

    // Defensive: pause all other gallery audios (belt-and-suspenders)
    var src = audio.src ? audio.src.split('/').pop().substring(0, 30) : '?';
    this.galleries.forEach(function(s, idx) {
      if (s !== state && s.audio && !s.audio.paused) {
        s.audio.pause();
        s.audioPlaying = false;
        log('_audioPlay: force-paused gallery[' + idx + '] (was still playing)');
      }
    });

    // Update state
    state.audioPlaying = true;
    this.isPlaying = true;
    this.panel = state.panel;
    this.element = ui.playBtn;

    // Update UI
    ui.playIcon.style.display = 'none';
    ui.pauseIcon.style.display = '';
    ui.playBtn.setAttribute('aria-label', 'Audio pausieren');

    // Play with fade-in (handle autoplay blocking)
    audio.volume = 0;
    // B3 (Ultrareview bug_001): globalen Mute respektieren statt hart
    // zu entmuten — sonst hebt jeder Play den Ton-Knopf wieder auf.
    audio.muted = !!this._muted;
    var self = this;
    audio.play().then(function() {
      // Fade in after play starts
    }).catch(function() {
      // play() was blocked (mobile autoplay policy) → reset state + UI
      log('Autoplay blocked — resetting state');
      state.audioPlaying = false;
      self.isPlaying = false;
      ui.playIcon.style.display = '';
      ui.pauseIcon.style.display = 'none';
      ui.playBtn.setAttribute('aria-label', 'Audio abspielen');
      clearInterval(state._fadeInterval);
      if (window.StoryMediaManager) {
        window.StoryMediaManager.notifyStop(self);
      }
    });
    this._audioFadeIn(state);

    log('_audioPlay →', src);
  };

  StoryGallery.prototype._audioPause = function(state) {
    var audio = state.audio;
    var ui = state.audioUI;
    if (!audio || !ui) return;

    state.audioPlaying = false;
    this.isPlaying = false;

    // Update UI
    ui.playIcon.style.display = '';
    ui.pauseIcon.style.display = 'none';
    ui.playBtn.setAttribute('aria-label', 'Audio abspielen');

    // Fade out then pause
    this._audioFadeOut(state, function() {
      audio.pause();
    });

    // Notify MediaManager
    if (window.StoryMediaManager) {
      window.StoryMediaManager.notifyStop(this);
    }

    log('Audio pause');
  };


  /* ========================================
     Audio — Fade In / Out (200ms, 4 steps)
     Pattern from audio-bubbles.js
     ======================================== */

  StoryGallery.prototype._audioFadeIn = function(state) {
    var audio = state.audio;
    clearInterval(state._fadeInterval);
    audio.volume = 0;

    var step = 0.25;
    state._fadeInterval = setInterval(function() {
      if (audio.volume < 1 - step) {
        audio.volume = Math.min(1, audio.volume + step);
      } else {
        audio.volume = 1;
        clearInterval(state._fadeInterval);
      }
    }, 50);
  };

  StoryGallery.prototype._audioFadeOut = function(state, callback) {
    var audio = state.audio;
    clearInterval(state._fadeInterval);
    var startVol = audio.volume;

    if (startVol <= 0) {
      if (callback) callback();
      return;
    }

    var step = startVol / 4;
    state._fadeInterval = setInterval(function() {
      if (audio.volume > step) {
        audio.volume = Math.max(0, audio.volume - step);
      } else {
        audio.volume = 0;
        clearInterval(state._fadeInterval);
        if (callback) callback();
      }
    }, 50);
  };


  /* ========================================
     Audio — MediaManager Contract Methods
     ======================================== */

  // Gallery handles panel lifecycle via its own event listeners,
  // but MediaManager.validateModule() requires these methods.
  StoryGallery.prototype.init = function() {};
  StoryGallery.prototype.start = function() {};

  // Called by MediaManager.enableAutoplay() during user gesture.
  // Previously called play() on ALL gallery audios to "unlock" them for Safari.
  // This caused multiple audios to start audibly on Safari (volume=0 unreliable).
  // Now: no pre-unlock. Gallery audio plays when the gallery is activated.
  // If Safari blocks autoplay, _audioPlay()'s .catch() resets UI → user taps play.
  StoryGallery.prototype.unlockAudio = function() {
    // Pre-load audio metadata (helps Safari with subsequent play calls)
    this.galleries.forEach(function(state) {
      if (state.audio) {
        state.audio.load();
      }
    });
    log('Audio elements preloaded (no play)');
  };

  StoryGallery.prototype.pause = function() {
    if (this.activeGallery && this.activeGallery.audio) {
      this._audioPause(this.activeGallery);
    }
  };

  StoryGallery.prototype.stop = function() {
    if (this.activeGallery && this.activeGallery.audio) {
      this._audioPause(this.activeGallery);
      this.activeGallery.audio.currentTime = 0;
    }
  };


  /* ========================================
     Audio — Time Formatting Helper
     ======================================== */

  StoryGallery._formatTime = function(seconds) {
    if (!seconds || !isFinite(seconds)) return '0:00';
    var m = Math.floor(seconds / 60);
    var s = Math.floor(seconds % 60);
    return m + ':' + (s < 10 ? '0' : '') + s;
  };


  /* ========================================
     Expose
     ======================================== */

  window.StoryModuleGallery = StoryGallery;

})(window);

/* AP-B/B3 (2026-07-23): globaler Mute vom MediaManager-Fan-out.
   WICHTIG (Ultrareview bug_001): die echten HTMLAudioElemente hängen NICHT
   im DOM — sie leben als new Audio() an den Galerie-States (state.audio);
   .story-gallery__audio ist nur der UI-Container-DIV. Deshalb über
   this.galleries iterieren. Anbau am exponierten Konstruktor (die
   Modul-IIFE ist hier schon geschlossen). */
if (window.StoryModuleGallery) {
  window.StoryModuleGallery.prototype.setMuted = function (muted) {
    this._muted = !!muted;
    var m = this._muted;
    (this.galleries || []).forEach(function (state) {
      if (state && state.audio) state.audio.muted = m;
    });
  };
}
